import { CDN_URL_ICONS } from '@/configs';
import CAlphaGovernor from '@/contracts/alphaGovernor';
import useTradeKey from '@/hooks/playerShareToken/useTradeKey';
import Avatar from '@/modules/AlphaPWA/Profiles/TradeKey/components/avatar';
import FieldAmount from '@/modules/AlphaPWA/Profiles/TradeKey/components/form/fieldAmount';
import FieldText from '@/modules/AlphaPWA/Profiles/TradeKey/components/form/fieldTextV2';
import InputWrapper from '@/modules/AlphaPWA/Profiles/TradeKey/components/form/inputWrapper';
import { MAX_DECIMAL } from '@/modules/AlphaPWA/Profiles/TradeKey/constants';
import { requestReload } from '@/state/common';
import { abbreviateNumber, formatName, requiredAmount } from '@/utils';
import { composeValidators } from '@/utils/form-validate';
import { getUrlAvatarTwitter } from '@/utils/helpers';
import { Box, Button, Flex, Text, useDisclosure } from '@chakra-ui/react';
import BigNumber from 'bignumber.js';
import { FormApi } from 'final-form';
import { forwardRef, PropsWithRef, useEffect, useRef, useState } from 'react';
import { Field, Form, useForm, useFormState } from 'react-final-form';
import { toast } from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { AMOUNT_TOKEN_PROPOSAL_TRANSFER } from '../constants';
import KeysModal from '../KeysModal';
import s from './styles.module.scss';

const validateAmountToken = (value = '0', values: any) => {
  const tokenBalance = values?.tokenBalance || 0;
  const needBalanceSend = new BigNumber(value);
  if (new BigNumber(needBalanceSend).gt(tokenBalance)) {
    return `Balance is not enough.`;
  }
  return undefined;
};

interface IFormModal extends PropsWithRef<any> {
  handleSubmit: any;
  submitting: boolean;
  circleTreasury: any;
  profile: any;
  userProfile: any;
}

const FormModal: React.FC<IFormModal> = forwardRef(
  ({ handleSubmit, submitting, circleTreasury, profile, userProfile }, ref) => {
    const { values, errors } = useFormState();
    const { change, blur } = useForm();

    const { isOpen, onOpen, onClose } = useDisclosure({
      id: 'keys_proposols_modal ',
    });
    const [forAnotherKey, setForAnotherKey] = useState<any>();

    useEffect(() => {
      if (userProfile && userProfile.address)
        change('forAddress', userProfile.address);
    }, [userProfile]);

    useEffect(() => {
      if (forAnotherKey && forAnotherKey.owner)
        change('forAddress', forAnotherKey.owner);
    }, [forAnotherKey]);

    useEffect(() => {
      if (circleTreasury.amountToken)
        change('tokenBalance', circleTreasury.amountToken);
    }, [circleTreasury]);

    useEffect(() => {
      if (values.amount) return;
      if (Number(circleTreasury.amountToken) > AMOUNT_TOKEN_PROPOSAL_TRANSFER) {
        change('amount', AMOUNT_TOKEN_PROPOSAL_TRANSFER);
      } else {
        change('amount', circleTreasury.amountToken);
      }
    }, [circleTreasury]);

    const handleChangeMaxBaseAmount = () => {
      const amount = new BigNumber(circleTreasury?.amountToken).toFixed(
        MAX_DECIMAL,
        BigNumber.ROUND_FLOOR
      ) as any;
      change('amount', amount);
      setTimeout(() => {
        blur('amount');
      }, 300);
    };

    const onClickKey = (data: any) => {
      setForAnotherKey(data);
    };

    const disableBtn = submitting || errors?.amount || errors?.desc;

    return (
      <form onSubmit={handleSubmit}>
        <Box mb={8} />

        <InputWrapper label="Title" className={s.inputWrapper}>
          <Field
            name="title"
            component={FieldText}
            validate={composeValidators(requiredAmount)}
            placeholder="Enter title"
            borderColor="transparent"
          />
        </InputWrapper>

        <Box mb={8} />

        <InputWrapper
          label="Requested amount"
          rightLabel={
            <Flex direction="row" gap={2} alignItems="center">
              <Text fontSize="12px !important" color="#BFBFBF !important">
                Treasury: {abbreviateNumber(circleTreasury?.amountToken)}
              </Text>
              <Avatar
                width={14}
                url={getUrlAvatarTwitter(
                  (profile?.twitterAvatar || profile?.avatar) as string,
                  'medium'
                )}
                address={profile?.address}
                name={profile?.twitterName}
                fontSize={6}
              />
            </Flex>
          }
          className={s.inputWrapper}
        >
          <Field
            name="amount"
            component={FieldAmount}
            validate={composeValidators(requiredAmount, validateAmountToken)}
            placeholder="0.0"
            decimals={6}
            borderColor="transparent"
          />
        </InputWrapper>
        {/* <PercentAmountSelector
          selectedAmount={values.amount}
          onChange={(value: string) => {
            change('amount', value);
          }}
          balance={circleTreasury?.amountToken}
          onMax={handleChangeMaxBaseAmount}
        /> */}

        <Box mb={8} />

        <Flex flexDirection="column" gap={2}>
          <Text fontSize={'12px'} fontWeight={500}>
            For
          </Text>
          <div className={s.btnSelectToken} onClick={onOpen}>
            <Flex
              alignItems={'center'}
              justifyContent={'space-between'}
              pb="8px"
              mt="8px"
            >
              <Flex gap={4} alignItems={'center'} w="100%">
                {forAnotherKey ? (
                  <Avatar
                    url={getUrlAvatarTwitter(
                      (forAnotherKey?.user_twitter_avatar ||
                        forAnotherKey?.avatar) as string,
                      'normal',
                      Boolean(forAnotherKey?.user_twitter_avatar)
                    )}
                    address={forAnotherKey?.owner}
                    name={forAnotherKey?.user_twitter_username}
                    width={24}
                    fontSize={8}
                  />
                ) : (
                  <Avatar
                    url={getUrlAvatarTwitter(
                      (userProfile?.twitter_avatar ||
                        userProfile?.avatar) as string,
                      'normal',
                      Boolean(userProfile?.twitter_avatar)
                    )}
                    address={userProfile?.address}
                    name={userProfile?.twitter_name}
                    width={24}
                    fontSize={8}
                  />
                )}
                <Flex gap={1} direction={'column'}>
                  <Flex alignItems={'center'} gap={2}>
                    <Text fontWeight={500}>
                      {forAnotherKey
                        ? formatName(
                            forAnotherKey?.user_twitter_name ||
                              (forAnotherKey?.twitterName as string),
                            50
                          )
                        : formatName(userProfile?.twitter_name as string, 50)}
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
            <img alt="caret" src={`${CDN_URL_ICONS}/caret-down.svg`} />
          </div>
        </Flex>

        <Box mb={8} />

        <InputWrapper label="Description" className={s.inputWrapper}>
          <Field
            name="desc"
            component={FieldText}
            validate={composeValidators(requiredAmount)}
            placeholder="Enter description"
            borderColor="transparent"
            inputType="textarea"
          />
        </InputWrapper>

        <Box mb={8} />

        <Button
          type="submit"
          w={'100%'}
          isDisabled={disableBtn}
          isLoading={submitting}
          loadingText={'Processing'}
        >
          Submit
        </Button>

        <KeysModal isOpen={isOpen} onClose={onClose} onItemClick={onClickKey} />
      </form>
    );
  }
);

const CreateModal = ({
  profile,
  circleTreasury,
  onClose,
  userProfile,
}: any) => {
  const [submitting, setSubmitting] = useState(false);
  const governorContract = useRef(new CAlphaGovernor()).current;
  const refForm = useRef<FormApi>();
  const dispatch = useDispatch();

  const onSubmit = async (values: any) => {
    if (!values.forAddress) return;
    try {
      setSubmitting(true);
      const _desc = JSON.stringify({
        title: values.title,
        desc: values.desc,
      });
      await governorContract.createProposal({
        governorAddress: profile?.governorAddress,
        passTokenAddress: profile?.passTokenAddress,
        treasuryAddress: profile?.passTokenAddress,
        circleOwnerAddress: values.forAddress,
        amount: values.amount,
        description: _desc,
      });

      toast.success('Submit a proposal successfully!');
      dispatch(requestReload());
      onClose && onClose();
    } catch (e) {
      console.log('Create Proposal', e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Flex className={s.container} direction={'column'}>
      <Text fontSize="24px" fontWeight={500} textAlign="center">
        Submit a proposal
      </Text>
      <Form onSubmit={onSubmit}>
        {({ handleSubmit }) => (
          <FormModal
            ref={refForm}
            handleSubmit={handleSubmit}
            submitting={submitting}
            circleTreasury={circleTreasury}
            profile={profile}
            userProfile={userProfile}
          />
        )}
      </Form>
    </Flex>
  );
};

export default CreateModal;
